import React, { Component, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { Radio, Table } from "antd";
import { getTopLawFirmsByLPIX } from "../../services/api-prosecution";
import { FlexibleWidthXYPlot, XAxis, YAxis, MarkSeries, Hint } from "@unifiedpatents/euclid-react";
import { getAttributeScale } from "react-vis/dist/utils/scales-utils";
import { sorterNumberGen } from "../../helpers/table";
import { sumBy } from "lodash";
import { toTitleCase } from "../../helpers/converters";

const SeparationLines = props => {
  const { _allData, innerHeight: height, innerWidth: width, marginTop, marginLeft } = props;
  const data = _allData[0];
  const scaler = {
    x: getAttributeScale(props, "x"),
    y: getAttributeScale(props, "y")
  };
  const avg = {
    x: scaler.x(sumBy(data, "x") / data.length),
    y: scaler.y(sumBy(data, "y") / data.length)
  };
  // gradient overlay from green (top-right) to red (bottom-left) to indicate good & bad, transparent in the middle (wider region)
  return avg.x > 0 && avg.y > 0 ? (
    <g transform={`translate(${marginLeft},${marginTop})`}>
      <defs>
        <linearGradient id="overlay-gradient" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="green" stopOpacity="0.5" />
          <stop offset="20%" stopColor="white" stopOpacity="0" />
          <stop offset="80%" stopColor="white" stopOpacity="0" />
          <stop offset="100%" stopColor="red" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={width} height={height} fill="url(#overlay-gradient)" pointerEvents="none" />
      <line stroke="grey" strokeOpacity={0.5} x1={0} x2={width} y1={avg.y} y2={avg.y} />
      <line stroke="grey" strokeOpacity={0.5} x1={avg.x} x2={avg.x} y1={0} y2={height} />
    </g>
  ) : null;
};
SeparationLines.propTypes = {
  _allData: PropTypes.array,
  innerHeight: PropTypes.number,
  innerWidth: PropTypes.number,
  marginTop: PropTypes.number,
  marginLeft: PropTypes.number
};
SeparationLines.displayName = "SeparationLines";
SeparationLines.requiresSVG = true;

const ArtUnitLawFirmsChart = ({ data, axisLabels = { x: "", y: "", z: "" }, axisOrder = { x: "desc", y: "desc" } }) => {
  const [hintValue, setHintValue] = useState(data[0]); // highlight top no.1 by default
  const xDomain = data.reduce((res, row) => ({ max: Math.max(res.max, row.x), min: Math.min(res.min, row.x) }), { min: Infinity, max: -Infinity });
  const yDomain = data.reduce((res, row) => ({ max: Math.max(res.max, row.y), min: Math.min(res.min, row.y) }), { min: Infinity, max: -Infinity });
  const domains = {
    xDomain: axisOrder.x === "desc" ? [xDomain.max, xDomain.min] : [xDomain.min, xDomain.max],
    yDomain: axisOrder.y === "desc" ? [yDomain.max, yDomain.min] : [yDomain.min, yDomain.max],
  }
  return (
    <FlexibleWidthXYPlot height={300} {...domains}>
      <XAxis title={axisLabels.x} />
      <YAxis title={axisLabels.y} />
      <SeparationLines />
      <MarkSeries data={data} onValueMouseOver={setHintValue} onValueMouseOut={() => setHintValue(null)} sizeRange={[5, 15]} color="rgb(24, 144, 255)" />
      {hintValue ? (
        <Hint value={hintValue}>
          <div className="rv-hint__content">
            <p>
              <b>{hintValue.label}</b>
            </p>
            <dl>
              <dd>#Apps: {hintValue.size}</dd>
              <dd>{axisLabels.x}: {hintValue.x.toFixed(2)}</dd>
              <dd>{axisLabels.y}: {hintValue.y.toFixed(2)}</dd>
              <dd>{axisLabels.z}: {hintValue.z.toFixed(2)}</dd>
            </dl>
          </div>
        </Hint>
      ) : null}
    </FlexibleWidthXYPlot>
  );
};
ArtUnitLawFirmsChart.propTypes = {
  data: PropTypes.array.isRequired,
  axisLabels: PropTypes.shape({
    x: PropTypes.string,
    y: PropTypes.string,
    z: PropTypes.string
  }),
  axisOrder: PropTypes.shape({
    x: PropTypes.oneOf(["asc", "desc"]),
    y: PropTypes.oneOf(["asc", "desc"])
  })
};

const CHART_CONFIGS = {
  "ar&p": {
    dataMapper: d => ({
      label: toTitleCase(d.lf),
      x: d.ar,
      y: d.p,
      z: d.n,
      size: d.t,
      opacity: 0.5 / d.n, // lower narrowness is more visible, lower bound at 0.1
      ar: d.ar,
    }),
    axisLabels: {
      x: "Allowance Rate",
      y: "Pendency (Years)",
      z: "Narrowness"
    },
    axisOrder: {
      x: "asc",
      y: "desc"
    }
  },
  "ar&n": {
    dataMapper: d => ({
      label: toTitleCase(d.lf),
      x: d.ar,
      y: d.n,
      z: d.p,
      size: d.t,
      opacity: 1 / d.p, // lower pendency is more visible
      ar: d.ar,
    }),
    axisLabels: {
      x: "Allowance Rate",
      y: "Narrowness",
      z: "Pendency (Years)"
    },
    axisOrder: {
      x: "asc",
      y: "desc"
    }
  },
  "n&p": {
    dataMapper: d => ({
      label: toTitleCase(d.lf),
      x: d.p,
      y: d.n,
      z: d.ar,
      size: d.t,
      opacity: d.ar * 0.9 + 0.1, // higher allowance rate is more visible
      ar: d.ar,
    }),
    axisLabels: {
      x: "Pendency (Years)",
      y: "Narrowness",
      z: "Allowance Rate"
    },
    axisOrder: {
      x: "desc",
      y: "desc"
    }
  },
};

class ArtUnitLawFirms extends Component {
  static propTypes = {
    artUnit: PropTypes.string.isRequired
  };

  state = {
    data: [],
    compare: "ar&p"
  };

  componentDidMount() {
    getTopLawFirmsByLPIX(50, { examiner_art_unit: this.props.artUnit })
      .then(data => this.setState({ data }))
      .catch(() => this.setState({ data: [] }));
  }

  render() {
    const { data, compare } = this.state;
    const chartConfig = CHART_CONFIGS[compare]
    return data && data.length ? (
      <div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <Radio.Group value={this.state.compare} onChange={e => this.setState({ compare: e.target.value })}>
            <Radio.Button value="ar&p">Effective & Efficient</Radio.Button>
            <Radio.Button value="ar&n">Effective & Elevated</Radio.Button>
            <Radio.Button value="n&p">Elevated & Efficient</Radio.Button>
          </Radio.Group>
          <ArtUnitLawFirmsChart
            data={data.map(chartConfig.dataMapper)}
            axisLabels={chartConfig.axisLabels}
            axisOrder={chartConfig.axisOrder}
          />
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 16 }}>
            <small>Experienced = large #Apps</small>
            <small style={{ fontWeight: compare.indexOf("ar") >= 0 ? 700 : 400 }}>Effective = high allowance rate</small>
            <small style={{ fontWeight: compare.indexOf("p") >= 0 ? 700 : 400 }}>Efficient = low pendency</small>
            <small style={{ fontWeight: compare.indexOf("n") >= 0 ? 700 : 400 }}>Elevated = working on broader patents</small>
          </div>
        </div>
        <Table
          size="small"
          rowKey="lf"
          dataSource={data}
          columns={[
            {
              dataIndex: "lf",
              title: "Law Firm",
              render: val => <Link to={`/prosecution/lawfirm/${val}`}>{toTitleCase(val)}</Link>
            },
            {
              dataIndex: "s",
              title: "LPIX",
              render: val => (val * 100).toFixed(1) + "%",
              sorter: sorterNumberGen("s")
            },
            {
              dataIndex: "t",
              title: "#Apps",
              sorter: sorterNumberGen("t")
            },
            {
              dataIndex: "ar",
              title: "Allowance Rate",
              render: val => (val * 100).toFixed(1) + "%",
              sorter: sorterNumberGen("ar")
            },
            {
              dataIndex: "p",
              title: "Pendency",
              sorter: sorterNumberGen("p"),
              render: val => val.toFixed(2)
            },
            {
              dataIndex: "n",
              title: "Narrowness",
              sorter: sorterNumberGen("n"),
              render: val => val.toFixed(2)
            }
          ]}
          scroll={{ x: 720 }}
        />
      </div>
    ) : (
      <p>No data available.</p>
    );
  }
}

export default ArtUnitLawFirms;
